import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiBlob } from "./api";
import { C } from "./pharmacy/look";

// The customer's prescription photo, fetched with the staff token (the API never
// serves it without one) and shown from memory: it is not cached or saved.
// Rotate for photos taken sideways; open full size in a new tab to zoom.

export function RxPhoto({ orderId, height = 520 }: { orderId: string; height?: number }) {
  const photo = useQuery({
    queryKey: ["rx", orderId],
    queryFn: () => apiBlob(`/staff/orders/${orderId}/prescription`),
    staleTime: Infinity,
    gcTime: 60_000,
    retry: 1,
  });
  const [url, setUrl] = useState<string | null>(null);
  const [turn, setTurn] = useState(0);
  useEffect(() => {
    if (!photo.data) return setUrl(null);
    const u = URL.createObjectURL(photo.data);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [photo.data]);

  const box: React.CSSProperties = {
    height,
    borderRadius: 14,
    background: C.tint,
    border: `1px solid ${C.line}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    color: C.muted,
    fontSize: 13,
    textAlign: "center",
    padding: 16,
    boxSizing: "border-box",
  };
  if (photo.isLoading) return <div style={box}>Loading the prescription…</div>;
  if (photo.isError) return <div style={box}>Could not load the prescription. Reload to try again.</div>;
  if (!url) return <div style={box}>The photo is no longer available (deleted 14 days after the order ended).</div>;

  const sideways = turn % 2 === 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ ...box, padding: 0, background: C.ink }}>
        <img
          src={url}
          alt={`Prescription for order ${orderId}`}
          style={{
            transform: `rotate(${turn * 90}deg)`,
            maxWidth: sideways ? height : "100%",
            maxHeight: sideways ? "100%" : height,
            objectFit: "contain",
          }}
        />
      </div>
      <div className="no-print" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" onClick={() => setTurn((t) => (t + 1) % 4)} style={smallButton}>
          Rotate
        </button>
        <a href={url} target="_blank" rel="noreferrer" style={{ ...smallButton, textDecoration: "none" }}>
          Open full size
        </a>
      </div>
    </div>
  );
}

const smallButton: React.CSSProperties = {
  minHeight: 40,
  padding: "0 14px",
  borderRadius: 999,
  border: `1px solid ${C.line}`,
  background: C.surface,
  color: C.ink,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
};
